import React, {useEffect, useState} from 'react'
import {customsTypes, RESHIP_CONVERTABLE_CURRENCIES, warehouseData} from "../common/constants";
import agent from "../api/agent";
import {handleError, round} from "../common/utils";
import {message, Tooltip, Select, Spin} from "antd";
import {remove} from "lodash";
import {InfoCircleOutlined} from '@ant-design/icons'
import {useSelector} from "react-redux";
import UploadIncomingVendorInvoices from './UploadIncomingVendorInvoices';
import usePackageDetails from '../hooks/usePackage';

const MobileDeclarationForm = ({pkg, saveValue, openForm, closeAction, saveCustoms, pkgCustomsType, onFinish }) => {
  const [page, setPage] = useState(1)
  const [declaredItems, setDeclaredItems] = useState([])
  const [customsType, setCustomsType] = useState(pkgCustomsType ? pkgCustomsType : 1)
  const [forexRates, setForexRates] = useState([])
  const [actionItem, setActionItem] = useState(null)
  const [actionType, setActionType] = useState(null)
  const [savedInvoices, setSavedInvoices] = useState([])

  const { 
    isLoading, 
    updatePackageCustomsType, 
    getItems, 
    updateItems, 
    addItem, 
    deleteItem 
  } = usePackageDetails()
  
  const itemDeclarationRules = useSelector(state => state.itemDeclarationRules)

  const DEFAULT_NEW_ITEM = {
    itemId: 'new',
    declaredName: '',
    declaredDescription: '',
    declaredIncomingVendor: '',
    declaredValue: '',
    declaredUnitValue: '',
    declaredQuantity: '',
    declaredOrigin: warehouseData(pkg.warehouse).countryCode,
    declaredFromCurrency: warehouseData(pkg.warehouse).currency,
    declaredToCurrency: warehouseData(pkg.warehouse).currency,
    declaredFxRate: 1,
    declaredFxValue: 0,
  }

  const IS_DELETE_BUTTON_DISABLED = [8].includes(pkg.disposition)

  useEffect(() => {
  }, [actionItem, declaredItems])

  useEffect(() => {
    getCustomsDeclarationData()
  }, [])

  const getCustomsDeclarationData = async () => {
    try {
      const items = await getItemsData()
      const forexRates = await getForexData()

      const sanitizedItems = await insertInitialForexRates(items, forexRates)

      if (!sanitizedItems.length) {
        sanitizedItems.push(DEFAULT_NEW_ITEM)
      }

      setDeclaredItems(sanitizedItems)
      setForexRates(forexRates)
    } catch (e) {
      handleError(e)
    }
  }

  const insertInitialForexRates = (items, forexRates) => {
    if (items && !items.length) {
      return []
    }

    if (forexRates && !forexRates.length) {
      return []
    }

    const declaredItemsClone = [...items]

    const sanitizedItems = declaredItemsClone.map(item => {

      const selectedForexRate = getForexRate(forexRates, item.declaredToCurrency, item.declaredFromCurrency)

      const rate = handleFXConversion(selectedForexRate)
      const fxValue = round((item.declaredValue * rate))
      return {
        ...item,
        declaredFromCurrency: item.declaredFromCurrency ? item.declaredFromCurrency : warehouseData(pkg.warehouse).currency,
        declaredToCurrency: warehouseData(pkg.warehouse).currency,
        declaredFxRate: rate,
        declaredFxValue: fxValue,
        declaredUnitValue: round(item.declaredValue/item.declaredQuantity, 2)
      }
    })
    return sanitizedItems
  }

  const getItemsData = async () => {
    const response = await getItems(pkg)
    const items = response.data

    return items
  }

  const getForexData = async () => {
    const { currency } = warehouseData(pkg.warehouse)
    const response = await agent.forex.getForexRates({ currency })
    const forexRates = response.data.data

    return forexRates
  }

  const getForexRate = (forexRates, toCurrency, fromCurrency) => {
    const selectedForexRate = forexRates.find(item => {
      const fxFromCurrency = fromCurrency ? fromCurrency : warehouseData(pkg.warehouse).currency
      const fxToCurrency = toCurrency ? toCurrency : warehouseData(pkg.warehouse).currency

      return item.fromCurrency === fxToCurrency && item.toCurrency === fxFromCurrency
    })
    return selectedForexRate
  }

  const handleFXConversion = (selectedForexRate) => {
    const rate = 1 / selectedForexRate.rate * 1
    return round(rate)
  }

  const updateCustomsType = async (value) => {
    if (pkg.locked === 1 || pkg.locked) {
      return
    }

    setCustomsType(value)
    saveCustoms(value)
    await updatePackageCustomsType({ packageId: pkg.packageId, outgoingCustomsType: value})
  }

  const CONVERTED_TOTAL = round(declaredItems.reduce((acc, val) => acc += val.declaredFxValue * 1, 0))

  const onAddItem = () => {
    setPage(2)
    setActionItem(DEFAULT_NEW_ITEM)
    setActionType('Add')
  }

  const onEditItem = (item) => {
    setPage(2)
    const itemWithUnitPrice = {
      ...item,
      declaredUnitValue: round(item.declaredValue/item.declaredQuantity, 2)
    }
    setActionItem(itemWithUnitPrice)
    setActionType('Edit')
  }

  const handleStringInputChange = (e) => {
    try {
      const declaredItemClone = actionItem
      const name = e.target.name
      const value = e.target.value.replace(/[^A-Za-z0-9 _]/, '').slice(0, 45)
      declaredItemClone[name] = value
      setActionItem(declaredItemClone)

    } catch (e) {
      handleError(e)
    }
  }

  const handleNumberInputChange = (e) => {

    try {
      const declaredItemClone = actionItem
      const name = e.target.name
      let value = e.target.value * 1

      if (isNaN(value)) {
        value = 0
      }

      setActionItem(prev => {
        const next = { ...prev };

        if (name === 'declaredUnitValue') {
          next.declaredUnitValue = value;
          next.declaredValue   = value * next.declaredQuantity;
        } else {
          next[name] = value;
        }

        next.declaredFxValue = round(next.declaredValue * next.declaredFxRate);
        return next;
      });


    } catch (e) {
      handleError(e)
    }
  }

  const handleDropdownInput = (e) => {
    try {
      const declaredItemClone = actionItem
      declaredItemClone.declaredFromCurrency = e

      const toCurrency = declaredItemClone.declaredToCurrency
      const fromCurrency = declaredItemClone.declaredFromCurrency

      const selectedForexRate = getForexRate(forexRates, toCurrency, fromCurrency)
      const rate = handleFXConversion(selectedForexRate)
      const fxValue = round((declaredItemClone.declaredValue * rate))
      declaredItemClone.declaredFxRate = rate
      declaredItemClone.declaredFxValue = fxValue
      setActionItem(declaredItemClone)

    } catch (e) {
      handleError(e)
    }
  }

  const addDeclaredItem = async () => {
    try {
      const addedItem = actionItem

      delete addedItem.itemId

      const requestObject = {
        packageId: pkg.packageId,
        item: addedItem,
        customsType
      }

      await addItem(requestObject)
      await getCustomsDeclarationData()
      setPage(1)

    } catch (e) {
      handleError(e)
    } finally {
      onFinish && onFinish()
    }
  }

  const updateDeclaredItem = async () => {
    try {
      if (!!actionItem) {
        const updateItemIndex = declaredItems.findIndex(item => item.itemId === actionItem.itemId)
        declaredItems[updateItemIndex] = actionItem
        declaredItems.forEach(item => {
          delete item.declaredUnitValue;
        });
        setDeclaredItems(declaredItems)
      }

      if (declaredItems.find(item => item.declaredValue === 0)) {
        message.error('Please input item value for declaration')
        return;
      }

      const requestObject = {
        packageId: pkg.packageId,
        items: declaredItems,
        customsType
      }
      
      await updateItems(requestObject)
      await getCustomsDeclarationData()
      setPage(1)

    } catch (e) {
      handleError(e)
    } 
  }

  const saveDeclaredItem = () => {
    try {

      if (!declaredItems.length) {
        return;
      }

      if (!customsType) {
        message.error('Please declare and save the type of items in your package.')
        return;
      }
      //Prevent processing checkout if declared value is 0
      if (declaredItems.find(item => item.declaredValue === 0)) {
        message.error('Please input item value in declaration form')
        return;
      }

      updateDeclaredItem()

      saveValue(CONVERTED_TOTAL)
      onFinish && onFinish()

    } catch (e) {
      handleError(e)
    } finally {
      closeAction()
    }
  }

  const goBackAction = () => {
    saveValue(CONVERTED_TOTAL)
    closeAction()
  }

  const goPrevPage = async () => {
    const items = await getItemsData()
    setDeclaredItems(items)
    setPage(1)
  }

  const deleteDeclaredItem = async () => {
    try {
      if (IS_DELETE_BUTTON_DISABLED) {
        return;
      }

      if (declaredItems.length === 0) {
        return;
      }

      if (pkg.locked === 1 || pkg.locked) {
        return
      }

      const [removedItem] = remove(declaredItems, (item) => {
        return item.itemId === actionItem.itemId
      })

      const requestObject = {
        packageId: pkg.packageId,
        itemId: removedItem.itemId
      }

      await deleteItem(requestObject)
      await getCustomsDeclarationData()

    } catch (e) {
      handleError(e)
    } finally {
      onFinish && onFinish()
      closeAction()
    }
  }

  const fetchSavedInvoices = async () => {
    try {
      const response = await agent.pkg.getInvoices({ packageId: pkg.packageId });
      setSavedInvoices(response.data.data || []);
    } catch (e) {
      handleError(e)
    }
  };

  const handleDeleteInvoice = async (packageInvoiceId) => {
    try {
      await agent.pkg.deleteInvoice({ packageInvoiceId });
      message.success('Invoice deleted successfully!');
    } catch (error) {
      handleError(error);
    } finally {
      fetchSavedInvoices()
    }
  };

  useEffect(() => {
    fetchSavedInvoices();
  }, [])

  return (
    <>
      {openForm && page === 1 &&
        <div className="declaration-item-list">
          <div className="declaration-item-list-top">
            <div className="declaration-item-customs">
              <h2>Contents</h2>
              <div className="pad-b-10 mt-3"> Which of the following best describes the contents of your package? </div>
              <div className="btn-toggle declaration-item-customs-type">
                {customsTypes.map(type => {
                  return (
                    <div
                      className={"btn declaration-item-type-option" + (customsType === type.value ? ' selectedType' : '')}
                      key={'customs-' + type.value} onClick={() => updateCustomsType(type.value)}>
                      {type.label}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="declaration-item-list-title">
              {itemDeclarationRules.enableAddItem &&
                <div className="btn-add" style={{color: '#3582c4'}} onClick={onAddItem}>
                  <i>
                    <svg class="feather">
                      <use href="/assets/images/icons/feather-sprite.svg#plus-circle"/>
                    </svg>
                  </i> Add Item
                </div>}
            </div>
            {isLoading ? <Spin/> :
              <div className="declaration-item-list-content">
                {declaredItems.length > 0 && declaredItems[declaredItems.length - 1].itemId !== 'new' && declaredItems.map((item, index) => {
                  return <div key={index} className="declaration-item-card" onClick={() => {
                    onEditItem(item)
                  }}>
                    <div className="declaration-item-card-left">{item.declaredDescription}</div>
                    <div className="declaration-item-card-right">
                      <div className="declaration-item-card-price">${item.declaredFxValue}</div>
                      <svg className="feather feather-light">
                        <use href="/assets/images/icons/feather-sprite.svg#chevron-right"/>
                      </svg>
                    </div>
                  </div>
                })}
              </div>
            }
          </div>
          <div className="declaration-item-list-bottom">
            <div className="declaration-item-value">
              <div>Total Value</div>
              <div
                className="declaration-item-value-total-price">${CONVERTED_TOTAL} {warehouseData(pkg.warehouse).currency}</div>
            </div>
            <div className="btn btn-green" onClick={() => saveDeclaredItem(declaredItems)}>Confirm Declaration & Pay Shipping</div>
          </div>
        </div>
      }

      {openForm && page === 2 && <div className="global-overlay">
          <div className="declaration-item-list global-overlay-modal">
            <div className="declaration-item-upper">
              <div className="declaration-item-page-header" onClick={goPrevPage}>
                <div className="btn btn-clear">
                  <i>
                    <svg className="feather">
                      <use href="/assets/images/icons/feather-sprite.svg#arrow-left"/>
                    </svg>
                  </i>
                  Go back
                </div>
              </div>
              <div className="declaration-item-list-title">
                {actionType === 'Edit' ? <h2>Edit Item</h2> : <h2>Add Item</h2>}
                {itemDeclarationRules?.enableEditIncomingVendor
                  && 
                  <UploadIncomingVendorInvoices
                    packageId={pkg.packageId} 
                    savedInvoices={savedInvoices}
                    callback={fetchSavedInvoices}
                  />}
              </div>
              <Spin spinning={isLoading}>
                <div className="declaration-item-edit-content">
                  {/* <div className="address-form-label">Item Name</div>
                    <input className="address-form-input" type="text" disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditName}
                           defaultValue={actionItem.declaredName} name='declaredName'
                           onChange={handleStringInputChange}/> */}
                  <div className="address-form-label">Item Description</div>
                  <input className="address-form-input" type="text"
                         disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditDescription}
                         defaultValue={actionItem.declaredDescription} name='declaredDescription'
                         onChange={handleStringInputChange}/>
                    {itemDeclarationRules?.enableEditIncomingVendor
                      && 
                      <div className="address-form-label">
                        Incoming Vendor
                        <Tooltip title="For shipments from the US to Canada, enter the vendor you purchased the item from (e.g., Target, Amazon, Walmart) for each item to unlock additional discounted shipping options at checkout.">
                          <InfoCircleOutlined style={{ marginLeft: '8px', fontSize: '16px' }} />
                        </Tooltip>
                      </div>
                    }
                    {
                      itemDeclarationRules?.enableEditIncomingVendor &&
                      <input className="address-form-input" type="text" 
                             disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditDescription}
                             defaultValue={actionItem.declaredIncomingVendor} name='declaredIncomingVendor'
                             onChange={handleStringInputChange}/>
                    }
                  <div className="declaration-item-row ">
                    <div className="pad-r-10 flexItem">
                      <div className="address-form-label">Qty</div>
                      <input className="address-form-input" type="text"
                             disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditQty}
                             defaultValue={actionItem.declaredQuantity}
                             name='declaredQuantity' onChange={handleNumberInputChange}/>
                    </div>
                    <div className="pad-l-10 flexItem">
                      <div className="address-form-label">Unit Price</div>
                     <input 
                      className="address-form-input" 
                      type="number" 
                      step="0.01" 
                      disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditValue} 
                      defaultValue={actionItem.declaredUnitValue}
                      name="declaredUnitValue"
                      onChange={handleNumberInputChange} 
                    />
                    </div>
                    <div className="pad-l-10 flexItem">
                      <div className="address-form-label">Item Value</div>
                      <input
                        className="address-form-input"
                        type="number"
                        step="0.01"
                        disabled={true}
                        value={actionItem.declaredValue}
                        name="declaredValue"
                      />
                    </div>
                  </div>
                  <div className="address-form-label">Currency</div>
                  <Select className="address-form-select"
                          defaultValue={actionItem.declaredFromCurrency ? actionItem.declaredFromCurrency : warehouseData(pkg.warehouse).currency}
                          onChange={handleDropdownInput}
                          name='declaredFromCurrency'
                          disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditCurrency}
                          options=
                            {RESHIP_CONVERTABLE_CURRENCIES.map((item, i) => ({value: item, label: item}))}
                  />
                  {
                    itemDeclarationRules?.enableEditIncomingVendor
                    && 
                    <div style={{ marginTop: '24px'}}>
                      <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
                        Uploaded Vendor Invoices ({savedInvoices.length})
                      </div>
                      {savedInvoices.length > 0 && (
                        <div style={{ paddingBottom: '5px' }}>
                          {savedInvoices.map(({ packageInvoiceId, url, fileName }) => (
                            <div key={packageInvoiceId} className="package-details-item">
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                {fileName}
                              </a>
                              <button
                                className="package-item-icon-col"
                                style={{ paddingLeft: '5px' }}
                                onClick={() => handleDeleteInvoice(packageInvoiceId)}
                              >
                                <i>
                                  <svg className="feather">
                                    <use href="/assets/images/icons/feather-sprite.svg#trash-2" />
                                  </svg>
                                </i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  }
                </div>
              </Spin>
            </div>
            <div className="declaration-item-edit-btn">
              <div className="btn btn-fill"
                   onClick={actionType === 'Add' ? addDeclaredItem : updateDeclaredItem}>Save Item
              </div>
            
              {actionType === 'Edit' && itemDeclarationRules?.enableDeleteItem &&
                <div className="btn btn-clear mt-3" onClick={deleteDeclaredItem}>
                  <i>
                    <svg className="feather">
                      <use href="/assets/images/icons/feather-sprite.svg#trash-2"/>
                    </svg>
                  </i>
                  Delete Item
                </div>}
            </div>
          
          </div>
      </div>}
    </>
  )
}

export default MobileDeclarationForm